import { Button, Text, Flex, Box, Link as ChakraLink } from '@chakra-ui/react';
import { ReactElement, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ModalClassGuideCreationVariantProps, SharedModalProps } from 'types/Modal';
import { ProtectedRoutes } from 'types/Routes';
import { MultiSelect } from 'components/common/Multiselect';
import { useUsers } from 'hooks/api/backend/managament/getB2BUsers';
import { useAddGuides } from 'hooks/api/backend/managament/getClasses';
import { CloseIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { toastContent } from 'config/toast';
import * as Sentry from '@sentry/react';
import { SchoolUserRole } from '@novaheal/types';
import Joi from 'joi';
import { Form } from 'components/common/Form/v2';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalClassGuideCreationVariantProps;

type FormData = {
  participants: string[];
};

const schema = Joi.object<FormData>({
  participants: Joi.array().items(Joi.string()).min(1).required().messages({
    'array.min': 'Bitte wähle mindestens eine Praxisanleitende aus.',
    'any.required': 'Bitte wähle mindestens eine Praxisanleitende aus.',
  }),
});

export const ClassGuideCreationModal = ({ handleClose, isShown, classId }: Props): ReactElement => {
  const { data: usersData, refetch } = useUsers({
    page: 1,
    searchQuery: '',
    sortBy: 'firstName',
    sortOrder: 'asc',
    excludeClassId: classId,
    role: [SchoolUserRole.GUIDE, SchoolUserRole.ADMIN],
    all: true,
  });

  const queryClient = useQueryClient();
  const addGuidesMutation = useAddGuides();
  const toast = useNovaToast();
  const [selectedGuides, setSelectedGuides] = useState<string[]>([]);

  useEffect(() => {
    if (isShown) {
      refetch();
    }
  }, [isShown, refetch]);

  const handleModalClose = (): void => {
    setSelectedGuides([]);
    handleClose();
  };

  const handleSubmit = async (data: FormData): Promise<void> => {
    if (!classId) {
      return;
    }

    try {
      const updatedClass = await addGuidesMutation.mutateAsync({
        classId,
        ids: data.participants,
      });
      // Update cache with the response data instead of refetching
      queryClient.setQueryData(['class', classId], updatedClass);
      toast.show({
        description: 'Praxisanleitende erfolgreich hinzugefügt',
        variant: ToastVariant.SUCCESS,
      });
      handleClose();
      setSelectedGuides([]);
    } catch (error) {
      toast.show({ ...toastContent.error.default, variant: ToastVariant.ERROR });
      Sentry.captureException(error);
    }
  };

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleModalClose}
      placement="center"
      size="md"
      hideHeader
    >
      <CustomModalContent p="30px" display="flex" flexDirection="column" gap="50px">
        <Form<FormData, typeof schema>
          onSubmit={handleSubmit}
          schema={schema}
          options={{ defaultValues: { participants: [] } }}
        >
          {({ setValue, formState: { errors } }) => (
            <Flex flexDir="column" gap="30px" width="100%">
              <Flex flexDir="column" gap="l" width="100%">
                <Flex position="relative" alignItems="flex-start" width="100%">
                  <Flex flexDir="column" gap="15px" flex="1">
                    <Text textStyle="h3.medium" color="text.primary">
                      Praxisanleitende hinzufügen
                    </Text>
                    <Text textStyle="body.m.regular" color="text.tertiary">
                      Wähle einen oder mehrere Benutzer aus, um sie als Praxisanleitende dem
                      ausgewählten Kurs zuzuweisen.
                    </Text>
                  </Flex>
                  <Box
                    position="absolute"
                    right="0"
                    top="0"
                    cursor="pointer"
                    onClick={handleModalClose}
                  >
                    <NovaIcon icon={CloseIcon} size="l" color="icon.secondary" aria-hidden />
                  </Box>
                </Flex>

                <Box width="100%">
                  <MultiSelect
                    placeholder="Namen auswählen"
                    options={
                      usersData?.data?.map((user) => ({
                        id: user.id,
                        label: `${user.firstName} ${user.lastName || ''}`.trim(),
                        email: user.email,
                      })) || []
                    }
                    identifierKey="id"
                    labelKey="label"
                    secondaryKey="email"
                    selectedValues={selectedGuides}
                    onChange={(values) => {
                      setSelectedGuides(values);
                      setValue('participants', values, { shouldValidate: true });
                    }}
                    searchable
                    searchPlaceholder="Benutzer suchen..."
                    emptyMessage={
                      <>
                        Kein Ergebnis gefunden oder es sind aktuell keine Praxisanleitende über
                        deine Organisations‑Lizenz verfügbar.{' '}
                        <ChakraLink asChild color="text.info" textDecoration="underline">
                          <Link to={ProtectedRoutes.ORG_MANAGEMENT_VOUCHER_DASHBOARD}>
                            Zur Lizenzverwaltung
                          </Link>
                        </ChakraLink>
                      </>
                    }
                  />
                  {errors.participants && (
                    <Text textStyle="body.xs.regular" color="text.error" mt="10px">
                      {errors.participants.message as string}
                    </Text>
                  )}
                </Box>
              </Flex>

              <Flex gap="5px" width="100%" justifyContent="flex-end">
                <Button
                  onClick={handleModalClose}
                  variant="tertiary"
                  px="xl"
                  py="10px"
                  fontWeight="regular"
                >
                  Abbrechen
                </Button>
                <Button type="submit" variant="primary" px="xl" py="10px">
                  Praxisanleitende hinzufügen
                </Button>
              </Flex>
            </Flex>
          )}
        </Form>
      </CustomModalContent>
    </CustomModal>
  );
};
